import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import {
  Car, Shield, Zap, Award, Smartphone, IndianRupee, MapPin, Check,
  Download, Star, ArrowRight, Sparkles, TrendingUp, Globe, User,
} from 'lucide-react';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, delay },
});

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="currentColor">
    <path d="M3.18 23.76c.3.17.64.24.99.2l13.7-11.96-2.85-2.85L3.18 23.76zM.54 1.18C.2 1.56 0 2.13 0 2.87v18.26c0 .74.2 1.31.54 1.69l.09.08 10.23-10.23v-.24L.63 1.1l-.09.08zM20.66 10.53l-2.91-1.66-3.19 3.19 3.19 3.19 2.93-1.67c.84-.48.84-1.57-.02-2.05zM4.17.24l13.58 7.74-2.85 2.85L4.17.24z" />
  </svg>
);

// ── Count-up hook ────────────────────────────────────────────────────────────
const useCountUp = (from, to, duration, trigger) => {
  const [val, setVal] = useState(from);
  useEffect(() => {
    if (!trigger) return;
    const t0 = performance.now();
    const diff = to - from;
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + diff * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps
  return val;
};

const COLOR_CLASSES = {
  teal: { text: 'text-teal-dark dark:text-teal', bg: 'bg-teal-tint dark:bg-teal/10', border: 'border-teal/25' },
  primary: { text: 'text-primary', bg: 'bg-primary-tint dark:bg-primary/10', border: 'border-primary/25' },
  accent: { text: 'text-accent-dark dark:text-accent', bg: 'bg-accent-tint dark:bg-accent/10', border: 'border-accent/25' },
  ink: { text: 'text-ink dark:text-dark-ink', bg: 'bg-surface-alt dark:bg-white/10', border: 'border-line dark:border-dark-line' },
};

const STATS = [
  { icon: IndianRupee, label: 'Commission', sub: 'Launch offer', color: 'teal', from: 25, to: 0, prefix: '', suffix: '%' },
  { icon: Shield, label: 'Platform Fee', sub: 'No fees, ever', color: 'primary', from: 50, to: 0, prefix: '₹', suffix: '' },
  { icon: Award, label: 'Driver Earns', sub: 'Of every fare', color: 'accent', from: 70, to: 100, prefix: '', suffix: '%' },
  { icon: Zap, label: 'Surge Pricing', sub: 'Near-zero', color: 'ink', from: 3, to: 0, prefix: '', suffix: '×' },
];

const StatCard = ({ icon: Icon, label, sub, color, from, to, prefix, suffix }) => {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const c = COLOR_CLASSES[color];

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const count = useCountUp(from, to, 1200, inView);

  return (
    <div ref={ref} className="p-5 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line text-center">
      <div className={`flex items-center justify-center w-11 h-11 rounded-control mx-auto mb-4 ${c.bg} border ${c.border}`}>
        <Icon size={20} className={c.text} />
      </div>
      <p className={`text-3xl font-black leading-none mb-1.5 ${c.text}`}>{prefix}{count}{suffix}</p>
      <p className="text-sm font-bold text-ink dark:text-dark-ink mb-0.5">{label}</p>
      <p className="text-xs text-ink-soft">{sub}</p>
    </div>
  );
};

const SERVICES = [
  { name: 'Toto', description: 'Traditional e-rickshaw rides for short distances', image: '/images/toto.png' },
  { name: 'Auto', description: 'Auto-rickshaw services across the city', image: '/images/auto.png' },
  { name: 'Cab', description: 'Comfortable car taxi for longer journeys', image: '/images/car.png' },
  { name: 'Bike', description: 'Quick two-wheeler rides through traffic', image: '/images/bike.png' },
];

const COMPARISON = [
  { feature: 'Commission', others: '20–30%', us: 'Lowest', icon: IndianRupee },
  { feature: 'Surge Pricing', others: '2–3× peak', us: '~Zero', icon: Zap },
  { feature: 'Hidden Fees', others: 'Yes', us: 'None', icon: Shield },
  { feature: 'Platform Fee', others: '₹10–30', us: '₹0', icon: Car },
  { feature: 'Direct Payment', others: 'No', us: 'Yes', icon: Check },
];

const JOURNEY = [
  { step: 1, title: 'Starting Point', city: 'Bhagalpur', desc: 'Launching in Bhagalpur, understanding and serving our local community first', icon: MapPin },
  { step: 2, title: 'State Expansion', city: 'Bihar', desc: 'Growing across Bihar, bringing driver-first rides to every district', icon: TrendingUp },
  { step: 3, title: 'National Vision', city: 'India', desc: 'Expanding across India, revolutionizing transportation nationwide', icon: Globe },
];

const About = () => {
  return (
    <>
      <Seo
        title="About TotoWala - India's Trusted Ride Booking App"
        description="Learn about TotoWala's mission to transform transportation in India. We connect riders and drivers across the nation with safe, affordable rides."
        canonical="/about"
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About TotoWala',
          description: "TotoWala is India's leading ride booking platform connecting riders and drivers across the nation.",
          url: 'https://totowala.in/about',
          publisher: { '@type': 'Organization', name: 'TotoWala Technologies Private Limited', url: 'https://totowala.in' },
        }}
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        {/* ── Hero ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
              className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              About TotoWala
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}
              className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto mb-7"
            >
              Bringing modern ride-booking to every doorstep. Lowest commission, no hidden fees, near-zero surge pricing. Drivers earn more here than anywhere else.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <a href="https://play.google.com/store/apps/details?id=com.elt.totowala" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-primary hover:bg-primary-dark transition-colors">
                <User size={16} /> Download for Riders <PlayStoreIcon />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-teal-dark hover:bg-teal transition-colors">
                <Car size={16} /> Download for Drivers <PlayStoreIcon />
              </a>
            </motion.div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              The Zero-Fee Promise
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {STATS.map((stat) => <StatCard key={stat.label} {...stat} />)}
            </div>
          </div>
        </section>

        {/* ── Mission & Vision ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Mission &amp; Vision
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { icon: Award, color: 'primary', title: 'Our Mission', text: 'To bring ride-booking service facilities to the doors of everyone, both riders and drivers, without any convenience fees or hidden charges. We aim to modernize small cities and help everyone adapt to technology without any cost barrier.' },
                { icon: Sparkles, color: 'teal', title: 'Our Vision', text: 'Starting from Bhagalpur, expanding across Bihar, and then throughout India, to create a transportation ecosystem where technology serves everyone equally, drivers earn better without commission cuts, and riders get affordable, transparent pricing.' },
              ].map(({ icon: Icon, color, title, text }) => {
                const c = COLOR_CLASSES[color];
                return (
                  <motion.div key={title} {...fadeUp(0)} className="p-7 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line">
                    <div className={`flex items-center justify-center w-11 h-11 rounded-control mb-4 ${c.bg} border ${c.border}`}>
                      <Icon size={20} className={c.text} />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-ink dark:text-dark-ink mb-2.5">{title}</h3>
                    <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Services ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Our Services
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {SERVICES.map(({ name, description, image }) => (
                <motion.div key={name} {...fadeUp(0)} className="p-6 rounded-card bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line text-center relative">
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-teal-tint dark:bg-teal/10 border border-teal/25">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal" />
                    <span className="text-[10px] font-semibold text-teal-dark dark:text-teal">Live</span>
                  </span>
                  <img src={image} alt={name} className="w-14 h-14 object-contain mx-auto mb-3" />
                  <h3 className="text-base font-bold text-ink dark:text-dark-ink mb-1.5">{name}</h3>
                  <p className="text-xs text-ink-soft leading-relaxed">{description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Comparison ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              TotoWala vs Others
            </h2>

            <div className="rounded-card overflow-hidden border border-line dark:border-dark-line bg-white dark:bg-dark-surface">
              <div className="grid grid-cols-3">
                <div className="p-4 flex items-end" />
                <div className="p-4 text-center border-l border-line dark:border-dark-line">
                  <p className="text-sm font-bold text-ink-soft dark:text-dark-ink-soft">Others</p>
                  <p className="text-[11px] text-ink-soft mt-0.5">Ola · Uber · Rapido</p>
                </div>
                <div className="p-4 text-center bg-primary-tint dark:bg-primary/10 border-l border-primary/25">
                  <p className="text-sm font-bold text-primary">TotoWala</p>
                  <p className="text-[11px] text-ink-soft mt-0.5">For the people</p>
                </div>
              </div>

              {COMPARISON.map(({ feature, others, us, icon: Icon }, i) => (
                <div key={feature} className={`grid grid-cols-3 border-t border-line dark:border-dark-line ${i % 2 === 1 ? 'bg-surface-alt dark:bg-white/[0.02]' : ''}`}>
                  <div className="flex items-center gap-2.5 p-4">
                    <Icon size={14} className="text-ink-soft shrink-0" />
                    <span className="text-sm font-medium text-ink dark:text-dark-ink">{feature}</span>
                  </div>
                  <div className="flex items-center justify-center p-4 border-l border-line dark:border-dark-line">
                    <span className="text-xs text-ink-soft">{others}</span>
                  </div>
                  <div className="flex items-center justify-center p-4 bg-primary-tint/40 dark:bg-primary/[0.04] border-l border-primary/15">
                    <span className="text-xs font-bold text-primary">{us}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Journey ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Our Journey
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {JOURNEY.map(({ step, title, city, desc, icon: Icon }) => (
                <motion.div key={step} {...fadeUp(step * 0.05)} className="p-6 rounded-card bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line">
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white text-sm font-bold mb-4">
                    {step}
                  </div>
                  <h3 className="text-base font-bold text-ink dark:text-dark-ink mb-1.5">{title}</h3>
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <Icon size={13} className="text-primary" />
                    <span className="text-xs font-semibold text-primary">{city}</span>
                  </div>
                  <p className="text-xs text-ink-soft leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Download CTA ── */}
        <section className="bg-ink py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 bg-white/10 border border-white/15 text-white/80">
              <span className="w-1.5 h-1.5 rounded-full bg-teal" />
              Started in Bihar · Expanding across India
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-3">
              Be part of the journey
            </h2>
            <p className="text-base text-white/65 leading-relaxed max-w-lg mx-auto mb-6">
              Driver-first pricing, total transparency. Join hundreds of drivers and riders already part of the movement.
            </p>

            <div className="flex items-center justify-center gap-1.5 mb-7">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill="var(--color-accent)" color="var(--color-accent)" />)}
              <span className="text-xs text-white/70 ml-1.5">Trusted by early adopters</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://play.google.com/store/apps/details?id=com.elt.totowala" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-sm bg-white text-ink hover:bg-white/90 transition-colors">
                <PlayStoreIcon /> Download for Riders <ArrowRight size={15} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-sm bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-colors">
                <PlayStoreIcon /> Download for Drivers <Download size={15} />
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default About;
